import { useState } from 'react';
import { useCanSeeWarehouses } from '../../store/auth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Card, Select, Button, Table, Tag, Space, Typography, message } from '../../ui';
import { request } from '../../api/client';
import { fmtQty } from '../../lib/format';
import { formatIstDate } from '../../lib/time';
import { PALETTE } from '../../lib/palette';
import { warehouseOption } from '../../lib/warehouseLabel';

/**
 * Admin stock-planning panel for an order: pick the dispatch warehouse and see warehouse-wise +
 * batch-wise SALEABLE availability with the FEFO-recommended batches. Soft allocation — choosing a
 * warehouse records it on the order; stock is only deducted at dispatch.
 */
export function StockPlanPanel({ orderId, dispatchWarehouseId, canEdit, onChanged }: {
  orderId: string;
  dispatchWarehouseId?: string | null;
  canEdit: boolean;
  onChanged?: () => void;
}) {
  const canSeeWarehouses = useCanSeeWarehouses();
  const qc = useQueryClient();
  const [wid, setWid] = useState<string | undefined>(dispatchWarehouseId ? String(dispatchWarehouseId) : undefined);

  const { data: warehouses } = useQuery({ queryKey: ['warehouses'], queryFn: () => request<any[]>({ url: '/warehouses/my' }), enabled: canSeeWarehouses });
  const { data: plan, isFetching } = useQuery({
    queryKey: ['order-stock-plan', orderId, wid],
    queryFn: () => request<any>({ url: `/orders/${orderId}/stock-plan`, params: wid ? { warehouseId: wid } : {} }),
  });

  const setWh = useConfirmedMutation({ title: "Dispatch this order from the selected warehouse?", content: "Stock is committed there, and the invoice bills under that warehouse’s GST registration.", okText: "Yes, use it" }, {
    mutationFn: (warehouseId: string) => request({ url: `/orders/${orderId}/dispatch-warehouse`, method: 'POST', data: { warehouseId } }),
    onSuccess: () => {
      message.success('Dispatch warehouse set');
      qc.invalidateQueries({ queryKey: ['order', orderId] });
      qc.invalidateQueries({ queryKey: ['order-stock-plan', orderId] });
      onChanged?.();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const lines: any[] = plan?.lines ?? [];
  const isCurrent = !!wid && String(dispatchWarehouseId ?? '') === String(wid);

  return (
    <Card title="Stock planning" style={{ marginBottom: 16 }}>
      <Space wrap style={{ marginBottom: 10 }}>
        <Select showSearch optionFilterProp="searchLabel" placeholder="Dispatch warehouse" style={{ width: 260 }}
          value={wid} onChange={setWid}
          options={(warehouses ?? []).map((w: any) => ({ value: String(w.id), ...warehouseOption(w) }))} />
        {canEdit && wid && (
          <Button variant="primary" loading={setWh.isPending} disabled={isCurrent} onClick={() => setWh.mutate(wid)}>
            {isCurrent ? 'Current dispatch warehouse' : 'Set as dispatch warehouse'}
          </Button>
        )}
        {plan && wid && (plan.fullyAvailableHere
          ? <Tag tone="green">Fully available here</Tag>
          : <Tag tone="gold">Short in this warehouse — transfer or backorder</Tag>)}
      </Space>
      <Table size="small" loading={isFetching} pagination={false} rowKey="orderItemId" dataSource={lines}
        locale={{ emptyText: wid ? 'No products' : 'Pick a warehouse to see availability' }}
        columns={[
          { title: 'Product', dataIndex: 'productName', render: (v: any, l: any) => v ?? l.productId },
          { title: 'Pending', dataIndex: 'pendingQty', align: 'right' as const, render: (v: any) => fmtQty(v) },
          { title: 'Available here', dataIndex: 'availableHere', align: 'right' as const, render: (v: any, l: any) => <span style={{ color: l.shortHere > 0 ? PALETTE.warning[600] : undefined }}>{fmtQty(v)}</span> },
          { title: 'Short', dataIndex: 'shortHere', align: 'right' as const, render: (v: any) => (v > 0 ? <b style={{ color: 'var(--color-warning-600)' }}>{fmtQty(v)}</b> : '—') },
          { title: 'Total (all WH)', dataIndex: 'availableTotal', align: 'right' as const, render: (v: any) => fmtQty(v) },
        ]}
        expandable={{
          rowExpandable: (l: any) => (l.batchesHere?.length ?? 0) > 0 || (l.byWarehouse?.length ?? 0) > 0,
          expandedRowRender: (l: any) => (
            <div>
              {(l.batchesHere?.length ?? 0) > 0 && (
                <>
                  <Typography.Text strong>Batches in this warehouse (FEFO):</Typography.Text>
                  <Table size="small" pagination={false} rowKey="batchId" dataSource={l.batchesHere} className="mt-1.5"
                    columns={[
                      { title: 'Batch', dataIndex: 'batchNo', render: (v: any, b: any) => <Space>{v ?? '—'}{(l.recommended ?? []).some((r: any) => String(r.batchId) === String(b.batchId)) && <Tag tone="green">FEFO</Tag>}</Space> },
                      { title: 'Mfg', dataIndex: 'mfgDate', render: (v: any) => (v ? formatIstDate(v) : '—') },
                      { title: 'Expiry', dataIndex: 'expDate', render: (v: any) => (v ? formatIstDate(v) : '—') },
                      { title: 'Available', dataIndex: 'available', align: 'right' as const, render: (v: any) => fmtQty(v) },
                      { title: 'Suggested', align: 'right' as const, render: (_: any, b: any) => { const r = (l.recommended ?? []).find((x: any) => String(x.batchId) === String(b.batchId)); return r ? <b style={{ color: 'var(--color-success-600)' }}>{fmtQty(r.qty)}</b> : '—'; } },
                    ]} />
                </>
              )}
              {(l.byWarehouse?.length ?? 0) > 1 && (
                <div style={{ marginTop: 8 }}>
                  <Typography.Text strong>Across warehouses: </Typography.Text>
                  {l.byWarehouse.map((w: any) => <Tag key={w.warehouseId}>{w.warehouseName}: {fmtQty(w.available)}</Tag>)}
                </div>
              )}
            </div>
          ),
        }} />
    </Card>
  );
}
